/**
 * User Management › Consultants (ops mock version 13, §B4; Jeff, 28 October 2026) — everyone in the consultant
 * programme, across every zone, and "Appoint a consultant".
 *
 * ⭐ THIS FILE WAS `PortalAccessTab.tsx` — the "Dashboard Access" tab (ADR-081 D11). That tab is gone: User Management
 * is people-first, and the person-centric access panel (ADR-113 D25, `PersonAccessPanel`) opens from the eye on any
 * row in any tab and remains the only place access is changed or removed. What the old tab held went where its people
 * are: the consultant list and the appoint form here; partner organisations to the Partners tab. The appoint form
 * (`AddOperatorPanel`, below) is MOVED, not forked — the same component, the same ADR-124 D4 country-picks-the-zone
 * control, the same wire.
 *
 * ⭐⭐ EVERY ZONE (ADR-122 Pattern 1). The read fans out; every consultant row carries the zone their sign-in was born
 * in, and every write goes back to that zone. Sydney's and Virginia's `nexa-consultant-programme` are separate orgs and
 * their people are never merged.
 *
 * Row actions: the eye (the person's access panel — removal happens there), Assign customers (the existing
 * assign-customers page, in the org's own zone), and Pause / Resume behind a confirmation — the same suspend /
 * reactivate verb the access panel offers on the consultant row (`updatePortalOperator`, portalType consultant).
 *
 * Fork-per-surface (§9): errors surface via the shared toast; the only inline error is the load-failure + Retry
 * pattern the sibling tabs use.
 */
import { useMemo, useState } from 'react';
import { useToast } from '../../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../../lib/reauth';
import { FilterPills } from '../../../components/FilterPills';
import { FilterSearch } from '../../../components/FilterSearch';
import { ColumnTooltip } from '../../../components/ColumnTooltip';
import { ZoneBoardLegend } from '../../../components/ZoneBoardLegend';
import { ZoneCell, ZoneFanoutWarning } from '../../../components/ZoneIndicators';
import { useZoneReadReport } from '../../../components/ZoneContext';
import { ZONES, consoleZoneLabel, useWorkingZone, zoneByCode, type PortalZone } from '../../../lib/zones';
import { ALL_ZONES, countInZone, matchesZoneFilter, zoneBoardLegend } from '../../../lib/zone-board';
import { ResidencyCountryField, resolveCreateZone } from '../../../components/ResidencyCountryField';
import { useZoneMap } from '../../../lib/zone-map';
import { zoneFor } from '../../../obw/residency';
import { actionErrorToast } from '../../../lib/friendly-error';
import {
  listPortalAccess,
  addPortalOperator,
  updatePortalOperator,
  operatorRoleLabel,
  PORTAL_TYPE_LABELS,
  ROLES_BY_PORTAL,
  OPERATOR_ROLE_DESCRIPTIONS,
  type PortalOrg,
  type PortalOperator,
  type PortalOperatorRole,
} from '../../../lib/portal-access-api';
import { ActionConfirmModal } from '../ActionConfirmModal';
import { ActionIcon, LoadFailed, Loading, PersonEye, fmtDate, useZoneList, type OpenPerson } from './shared';

/** One consultant, flattened out of their zone's consultant org. */
export interface ConsultantRow extends PortalOperator {
  orgId: string;
  zone: string;
}

type StatusKey = 'active' | 'pending' | 'suspended';

/**
 * ⭐⭐ ONE ROW, ONE STATUS (UAT run 4, 10 August 2026) — precedence, not two badges:
 *   1. suspended                → "Paused"   (a staff decision outranks an account-lifecycle detail)
 *   2. FORCE_CHANGE_PASSWORD    → "Pending sign-in" (appointed, never signed in — never alongside Active)
 *   3. otherwise                → "Active"
 */
export const consultantStatus = (op: Pick<PortalOperator, 'status' | 'cognitoStatus'>): StatusKey =>
  op.status !== 'active' ? 'suspended' : op.cognitoStatus === 'FORCE_CHANGE_PASSWORD' ? 'pending' : 'active';
const STATUS_BADGE: Record<StatusKey, [string, string]> = {
  active: ['portal-users-status-active', 'Active'],
  pending: ['portal-users-status-pending-password', 'Pending sign-in'],
  suspended: ['portal-badge-paused', 'Paused'],
};
const ALL_STATUSES: StatusKey[] = ['active', 'pending', 'suspended'];

const readConsultants = async (z: PortalZone): Promise<ConsultantRow[]> =>
  (await listPortalAccess(z))
    .filter((o) => o.portalType === 'consultant')
    .flatMap((o) => o.operators.map((op) => ({ ...op, orgId: o.orgId, zone: z.code })));

export function ConsultantsTab({ onOpenPerson }: { onOpenPerson: OpenPerson }) {
  const toast = useToast();
  const reauth = useReAuth();
  const { rows, results, failed, failedZones, reload } = useZoneList<ConsultantRow>(readConsultants, 'the consultants');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<StatusKey>>(new Set(ALL_STATUSES));
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [confirm, setConfirm] = useState<{ row: ConsultantRow; op: 'suspend' | 'reactivate' } | null>(null);
  const [busy, setBusy] = useState(false);

  const all = useMemo(() => rows ?? [], [rows]);
  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((c) => selected.has(consultantStatus(c)) && matchesZoneFilter(c, zoneFilter)
        && (!q || `${c.displayName ?? ''} ${c.email}`.toLowerCase().includes(q)))
      .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));
  }, [all, selected, zoneFilter, search]);
  const count = (k: StatusKey) => all.filter((c) => consultantStatus(c) === k).length;
  const nameOf = (c: PortalOperator) => c.displayName || c.email || c.cognitoSub;

  const run = async () => {
    if (!confirm) return;
    const { row, op } = confirm;
    const zone = zoneByCode(row.zone);
    if (!zone) return;
    setBusy(true);
    try {
      // ⭐ THE WRITE GOES TO THE CONSULTANT'S OWN ZONE — the row lives there and nowhere else.
      await reauth.withReauth(() => updatePortalOperator(row.cognitoSub, { portalType: 'consultant', orgId: row.orgId, op }, zone));
      toast.push({
        variant: 'success',
        title: op === 'suspend' ? 'Consultant paused' : 'Consultant resumed',
        detail: op === 'suspend'
          ? `${nameOf(row)}'s Consultant dashboard is hidden and its access blocked. Their customers' consent grants are untouched.`
          : `${nameOf(row)} can use the Consultant dashboard again.`,
      });
      setConfirm(null);
      void reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      toast.push({ variant: 'error', ...actionErrorToast(err, {
        action: op === 'suspend' ? 'Pause consultant' : 'Resume consultant', subject: nameOf(row), zoneLabel: consoleZoneLabel(row.zone),
      }) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="users-tab-panel" aria-label="Consultants" data-testid="users-tab-consultants">
      <section className="portal-uat-field portal-staff-add" aria-label="Appoint a consultant">
        <div className="portal-uat-field-header"><h2>Appoint a consultant</h2></div>
        <p className="portal-uat-field-help">
          Appoints the person into the consultant programme in the zone their country belongs to — the activation email,
          then the Consultant dashboard on first sign-in. Assigning them to subscriber accounts happens from their row.
        </p>
        <AddOperatorPanel portalType="consultant" orgName={PORTAL_TYPE_LABELS.consultant} partnerOrgs={[]} onAdded={() => void reload()} />
      </section>

      <h2 className="users-tab-heading">
        Consultants
        {rows && <span className="portal-badge">{all.length} · {count('active')} active · {count('pending')} pending sign-in · {count('suspended')} paused</span>}
      </h2>
      <p className="panel-help">
        Everyone in the consultant programme, in the zone their sign-in was born in. A consultant serves that zone&rsquo;s
        customers only. Being a consultant adds a dashboard to the person&rsquo;s sign-in — it never grants access to any
        client account; that always flows through the customer&rsquo;s own consent. The eye opens the person&rsquo;s access
        panel — their programme membership, the subscriber accounts they are assigned to, and removal.
      </p>

      <div className="portal-applications-controls">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search name or email…" ariaLabel="Search consultants by name or email" />
        <FilterPills
          ariaLabel="Filter consultants by status"
          selected={selected}
          onChange={setSelected}
          groups={[
            { key: 'active', label: 'Active', icon: '✅', count: count('active') },
            { key: 'pending', label: 'Pending sign-in', icon: '⏳', count: count('pending') },
            { key: 'suspended', label: 'Paused', icon: '⏸️', count: count('suspended') },
          ]}
        />
      </div>
      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Zone
          <select className="portal-select" aria-label="Zone filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value={ALL_ZONES}>All zones</option>
            {ZONES.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
          </select>
        </label>
      </div>
      <ZoneFanoutWarning failedZones={failedZones} noun="consultants" onRetry={() => void reload()} />
      <ZoneBoardLegend
        testId="users-consultants-legend"
        ariaLabel="Consultants by zone"
        loading={rows === null}
        columns={[{ key: 'all', label: 'Consultants' }, { key: 'active', label: 'Active' }, { key: 'pending', label: 'Pending sign-in' }, { key: 'suspended', label: 'Paused' }]}
        rows={zoneBoardLegend(results, {
          counts: (code) => ({
            all: countInZone(all, code),
            active: countInZone(all, code, (c) => consultantStatus(c) === 'active'),
            pending: countInZone(all, code, (c) => consultantStatus(c) === 'pending'),
            suspended: countInZone(all, code, (c) => consultantStatus(c) === 'suspended'),
          }),
        })}
      />
      <p className="legend-cap">Counted over every row, whatever the filter shows.</p>

      {rows === null && <Loading text="Loading the consultants…" />}
      {failed && <LoadFailed what="the consultants" onRetry={() => void reload()} />}
      {rows !== null && !failed && (
        <table className="portal-users-table" data-testid="users-consultants-table">
          <thead>
            <tr>
              <th scope="col">Consultant</th>
              <th scope="col">Email</th>
              <th scope="col">Zone</th>
              <th scope="col">
                <span className="portal-col-help-wrap">Status
                  <ColumnTooltip columnName="Status" items={[
                    { term: 'Active', desc: 'Signed in; the Consultant dashboard is live.' },
                    { term: 'Pending sign-in', desc: 'Appointed, but they have not set their password or signed in yet.' },
                    { term: 'Paused', desc: 'The dashboard is hidden and its access blocked; resume any time.' },
                  ]} />
                </span>
              </th>
              <th scope="col">Appointed</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={6} className="portal-users-empty">
                {selected.size === 0 ? 'No filter pills selected — click a status pill to show consultants.' : 'No consultants match the current filters.'}
              </td></tr>
            )}
            {shown.map((c) => {
              const sk = consultantStatus(c);
              const [cls, word] = STATUS_BADGE[sk];
              return (
                <tr key={`${c.zone}:${c.cognitoSub}`} data-status={sk}>
                  <td>
                    {/* The email has its own column — a consultant with no display name reads "—" here, not the address twice. */}
                    <div className="portal-users-name">{c.displayName || '—'}</div>
                    {c.enabled === false && <div className="portal-users-staffid"><span className="portal-badge portal-users-status-disabled">Sign-in disabled</span></div>}
                  </td>
                  <td>{c.email || '—'}</td>
                  <td><ZoneCell zone={c.zone} /></td>
                  <td><span className={`portal-badge ${cls}`}>{word}</span></td>
                  <td className="portal-users-created">{fmtDate(c.grantedAt)}</td>
                  <td className="portal-users-row-actions portal-actions-col">
                    <div className="portal-actions-row">
                      <PersonEye name={nameOf(c)} onOpen={() => onOpenPerson(c.cognitoSub, c.zone)} />
                      {/* The consultant's Manage: the assign-customers surface, in the ORG's zone — consultants serve
                          their own data region (ADR-116 Status note). */}
                      <ActionIcon
                        act="assign"
                        icon="assign"
                        label={`Assign customers to ${nameOf(c)}`}
                        tip="Assign to a subscriber account"
                        href={`/admin/users/portal-access/${encodeURIComponent(c.cognitoSub)}/assign?orgId=${encodeURIComponent(c.orgId)}&name=${encodeURIComponent(nameOf(c))}&zone=${encodeURIComponent(c.zone)}`}
                      />
                      {c.status === 'active' ? (
                        <ActionIcon act="pause" icon="pause" label={`Pause ${nameOf(c)}`} tip="Pause"
                          onClick={() => setConfirm({ row: c, op: 'suspend' })} />
                      ) : (
                        <ActionIcon act="resume" icon="resume" label={`Resume ${nameOf(c)}`} tip="Resume"
                          onClick={() => setConfirm({ row: c, op: 'reactivate' })} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <ActionConfirmModal
        open={confirm !== null}
        title={confirm?.op === 'suspend' ? 'Pause this consultant?' : 'Resume this consultant?'}
        intent={confirm?.op === 'suspend' ? 'warning' : 'primary'}
        primaryLabel={confirm?.op === 'suspend' ? 'Pause' : 'Resume'}
        loading={busy}
        onConfirm={() => void run()}
        onCancel={() => setConfirm(null)}
      >
        {confirm && (confirm.op === 'suspend'
          ? <p><b>{nameOf(confirm.row)}</b>&rsquo;s Consultant dashboard is hidden and its access blocked while paused. Their own sign-in and their customers&rsquo; consent grants are untouched. Resume any time from this table.</p>
          : <p><b>{nameOf(confirm.row)}</b> can use the Consultant dashboard again from their next sign-in.</p>)}
      </ActionConfirmModal>
    </section>
  );
}

// ---------------------------------------------------------------------------

function AddOperatorPanel({
  portalType,
  orgName,
  partnerOrgs,
  onAdded,
}: {
  portalType: PortalOrg['portalType'];
  orgName: string;
  partnerOrgs: PortalOrg[];
  onAdded: () => void;
}) {
  const org = { portalType, name: orgName };
  const toast = useToast();
  const reauth = useReAuth();
  /**
   * ⭐ ADR-124 D3/D4 — THE CONSULTANT'S OWN COUNTRY PICKS THEIR ZONE. A consultant can only ever
   * serve the zone they were created in (commercial policy, D3), so the zone is not a free choice:
   * it follows from the country, through the served map, as every other home does.
   */
  const [country, setCountry] = useState('');
  const zoneMap = useZoneMap();
  const workingZone = useWorkingZone();
  const derived = country && zoneMap ? zoneFor(zoneMap, country) : null;
  const zone = resolveCreateZone(derived?.zone);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const roles = ROLES_BY_PORTAL[org.portalType];
  const [role, setRole] = useState<PortalOperatorRole>(roles[0]);
  const [scope, setScope] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * ⭐⭐ ADR-116 D-C5 + D-A5 — APPOINTING A CONSULTANT TAKES AN EMAIL AND A NAME.
   *
   * Nothing else, because nothing else exists to take. `appointConsultant()` —
   * the function this form's only live arm actually calls — has the signature
   * `{ cognitoSub, displayName, businessName?, email, createdBy }`. Role and
   * visibility were never parameters of it:
   *   · ROLE — a consultant is solo and personal (D-A5: holding the role is not
   *     account membership, so there is no org and no colleague to outrank).
   *     D-C5 gives exactly one role: consultant or not. `role` was nonetheless a
   *     REQUIRED field on the wire, validated against the management roles and
   *     then discarded unread.
   *   · VISIBILITY — 'assigned' vs 'full' scoped a book that no longer works that
   *     way (D-C4: connection rows are the single source of truth). A consultant
   *     sees exactly the accounts that granted them, and there is no other set to
   *     widen to. The field was destructured server-side and never referenced.
   *
   * ⛔ THE TEST IS "WHAT DOES THE WRITE ACCEPT", NOT "WHAT LOOKS TIDY". These two
   * controls sat on the form for weeks looking like configuration; both were
   * inert on submit, and the visibility twin on the row above returned an error
   * every time it was used.
   */
  const isConsultant = org.portalType === 'consultant';

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // ⭐⭐ ADR-116 D-A4 (9 August 2026) — THERE IS NO PARTNER ROSTER TO ADD TO.
  // Partner standing is a property of the ACCOUNT: everyone who is a member of an
  // account holding Partner status has the Partner Dashboard, so partner access
  // is changed by changing who is a member of the partner's own account.
  //
  // ⛔ THE FORM IS REPLACED, NOT LEFT TO FAIL. `addPortalOperator` now refuses
  // every partner enrolment with `partner_enrolment_retired`, so leaving the form
  // in place would give staff a button whose only outcome is an error toast — the
  // mirror of the failure this codebase already records as "a button that
  // silently does nothing", and no better for being loud. Saying where to go
  // instead is the whole job of this panel now.
  if (org.portalType === 'partner') {
    return (
      <div className="portal-uat-field-form portal-users-add-operator">
        <p className="portal-muted">
          Partner access follows account membership. Everyone who is a member of{' '}
          {org.name}’s Nexanomics account has the Partner Dashboard — an account admin can act,
          every other member can view. There is no separate operator list to maintain here.
        </p>
        <p className="portal-muted">
          To change who has partner access, change who is a member of that account, on the
          account’s own Team surface.
        </p>
      </div>
    );
  }

  const submit = async () => {
    if (!zone) return; // unreachable: the button is disabled without a derived zone
    setBusy(true);
    try {
      const res = await reauth.withReauth(() =>
        addPortalOperator({
          jurisdiction: country,
          portalType: org.portalType,
          email: email.trim(),
          displayName: name.trim() || undefined,
          // Omitted entirely for consultant — `role` is optional server-side
          // precisely so this client need not invent a value to satisfy a field
          // the handler discards.
          ...(isConsultant ? {} : { role }),
          ...(scope && org.portalType === 'beta' ? { scopePartnerOrgId: scope } : {}),
        }, zone),
      );
      // F5 — the existing-subscriber path now sends a branded access-granted
      // notification; surface a send failure honestly instead of implying silence.
      const emailNote = res.emailSent === false
        ? ' The notification email did NOT send — let them know directly.'
        : ' A notification email has been sent.';
      toast.push({
        variant: res.emailSent === false ? 'info' : 'success',
        title: `${isConsultant ? 'Consultant appointed' : 'Operator enrolled'} — ${res.email} (${zone.label})`,
        detail: res.createdIdentity
          ? `A new identity was created and an invitation email sent. The ${PORTAL_TYPE_LABELS[org.portalType]} dashboard appears on first sign-in.`
          : `Their existing sign-in now shows the ${PORTAL_TYPE_LABELS[org.portalType]} dashboard.${emailNote}`,
      });
      setEmail('');
      setName('');
      setScope('');
      onAdded();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const msg = err instanceof Error ? err.message : String(err);
      const friendly = msg.includes('operator_already_enrolled')
        ? 'That person is already an operator of this dashboard.'
        // ADR-116 D-A5 — say WHY, and say what to do instead. "Could not enrol
        // the operator. subscriber_account_exists" is a raw error code in front
        // of a staff member who has done nothing wrong.
        : msg.includes('subscriber_account_exists')
        ? 'That email already has a Nexanomics subscriber account. A consultant is a personal role and cannot also hold an account — appoint them on a different email address.'
        : `Could not enrol the operator. ${msg}`;
      toast.push({ variant: 'error', title: 'Enrolment failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-uat-field-form portal-users-add-operator">
      {/* ADR-124 D4 — the appointee's own country; their zone follows from it. */}
      <ResidencyCountryField
        id="add-operator-country"
        value={country}
        onChange={setCountry}
        map={zoneMap}
        workingZone={workingZone}
        disabled={busy}
      />
      <label className="portal-uat-field-label">
        <span>Email Address</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoComplete="off"
        />
      </label>
      <label className="portal-uat-field-label">
        <span>Display Name (Optional)</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Casey Operator" />
      </label>
      {!isConsultant && (
        <label className="portal-uat-field-label">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as PortalOperatorRole)} aria-label="Operator role">
            {roles.map((r) => (
              <option key={r} value={r}>{operatorRoleLabel(r)}</option>
            ))}
          </select>
        </label>
      )}
      {org.portalType === 'beta' && (
        <label className="portal-uat-field-label">
          <span>Visibility</span>
          <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Operator visibility scope">
            <option value="">Full org — every granted client</option>
            {partnerOrgs.map((p) => (
              <option key={p.orgId} value={p.orgId}>
                Scoped: {p.name}’s clients only
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="portal-muted portal-users-role-hint">
        {isConsultant
          ? 'A consultant is a single person, not an organisation. They see only the accounts that have granted them access — nothing else — and there is one consultant role, so there is nothing to configure here. Appointing them issues their permanent invite code.'
          : OPERATOR_ROLE_DESCRIPTIONS[role]}
      </p>
      <div className="portal-uat-field-actions">
        <button
          type="button"
          className="portal-btn"
          onClick={() => void submit()}
          disabled={busy || !emailValid || !zone}
        >
          {/* ADR-116 UAT — the Consultant panel appoints a CONSULTANT, and says
              so. Partner keeps its own wording (its panel has no Add at all
              under D-A4, but the label stays generic for any future arm). */}
          {busy ? 'Working…' : isConsultant ? 'Add Consultant' : 'Add operator'}
        </button>
        <span className="portal-uat-field-cost">
          {/* ⭐⭐ ADR-116 D-A5 — THE OLD COPY IS NOW FALSE FOR A CONSULTANT.
              "An existing subscriber keeps their sign-in — the dashboard simply
              appears" described the partner/beta behaviour, where the person
              legitimately IS an account member. A consultant is a personal role
              with no account concept and dual-role is not permitted, so an email
              that already has a subscriber account is REFUSED rather than quietly
              granted. Leaving the promise on screen would have told staff the
              opposite of what the server now does. */}
          {isConsultant
            ? 'A consultant is a new, personal sign-in. An email that already has a Nexanomics account cannot be appointed — a consultant never doubles as a subscriber.'
            : 'An existing subscriber keeps their sign-in — the dashboard simply appears. A new email gets an invitation.'}
        </span>
      </div>
    </div>
  );
}
